import { Injectable } from "@angular/core";
import { SocketService } from "@main-services/socket.service";
import { HasSocketEvent, Model } from "@models";
import { SocketEvent } from "@helpers/types";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { CheckoutNavigationService } from "@modules/checkout/services/checkout-navigation.service";
import {
	AmountType,
	CheckoutRouteName,
	CheckoutQueryParamName,
	CheckoutQueryParamsCollection
} from "@modules/checkout/entities/checkout.model";
import { UrlTree } from "@angular/router";
import { CheckoutDepositState } from "@modules/checkout/entities/checkout.deposit-state";
import { CheckoutDepositData } from "@modules/checkout/entities/checkout.deposit-data";
import { Observable, Subscription } from "rxjs";
import { ApicallService } from "@core-services/apicall.service";
import { map, take } from "rxjs/operators";
import { MarginTradingRouteName } from "@modules/margin-trading/entities/margin-trading.model";
import { MT_QUERY_PARAMS } from "@helpers/constants";

@UntilDestroy()
@Injectable({ providedIn: "root" })
export class CheckoutDepositWebsocketStateService {
	private socketSub: Subscription = null;
	private timer: ReturnType<typeof setTimeout> = null;

	constructor(
		private readonly socketService: SocketService,
		private readonly checkoutNavigationService: CheckoutNavigationService,
		private readonly apicallService: ApicallService
	) {}

	public unsubscribeFromSockets(): void {
		if (this.socketSub) {
			this.socketSub.unsubscribe();
		}
	}

	public initSocketService(checkoutDepositData: CheckoutDepositData): void {
		this.socketSub = this.socketService
			.joinRoom<DepositStateSocketResponse>({
				name: "payments_deposit_status",
				type: DepositStateSocketResponse,
				isPrivate: true
			})
			.pipe(untilDestroyed(this))
			.subscribe((event) => {
				//we stop the timout because at this point because an event has been emitted
				clearTimeout(this.timer);

				switch (event.status) {
					case CheckoutDepositState.SUCCESS:
						//Checking margin trading query params in local storage
						//TODO: remove this after BE allows Margin Trading to use new checkout
						if (window.localStorage.getItem(MT_QUERY_PARAMS)) {
							const query = JSON.parse(window.localStorage.getItem(MT_QUERY_PARAMS));
							// use async navigation to remove local storage
							this.checkoutNavigationService
								.gotoByNavigationAsync(MarginTradingRouteName.MarginTradingBuySummary, {
									...query,
									fromDeposit: true
								})
								.finally(() => window.localStorage.removeItem(MT_QUERY_PARAMS));
							break;
						}

						if (checkoutDepositData) {
							const query: Partial<CheckoutQueryParamsCollection> = {
								[CheckoutQueryParamName.AssetId]: checkoutDepositData.assetId,
								[CheckoutQueryParamName.BuyAmount]: checkoutDepositData.amount,
								[CheckoutQueryParamName.FiatId]: checkoutDepositData.fiatId,
								[CheckoutQueryParamName.FiatWalletId]: checkoutDepositData.fiatWalletId,
								[CheckoutQueryParamName.WalletGuid]: checkoutDepositData.walletId,
								[CheckoutQueryParamName.selectedAmountType]: AmountType.fiat
							};
							this.checkoutNavigationService.gotoByNavigation(CheckoutRouteName.Summary, query);
						}
						break;
					case CheckoutDepositState.POTENTIAL_FRAUD:
						//TODO: remove this line after BE allows new
						this.cleanMYQueryParamsLocalStorage();

						this.checkoutNavigationService.gotoByNavigation(CheckoutRouteName.CheckoutDepositPaymentOnHold);
						break;
					case CheckoutDepositState.MANUAL_APPROVAL:
						//TODO: remove this line after BE allows new
						this.cleanMYQueryParamsLocalStorage();

						if (checkoutDepositData?.assetId) {
							this.checkoutNavigationService.gotoByNavigation(
								CheckoutRouteName.CheckoutDepositPaymentUnderReview,
								{ assetId: checkoutDepositData.assetId }
							);
						}
						break;
					case CheckoutDepositState.FAILURE:
						//TODO: remove this line after BE allows new
						this.cleanMYQueryParamsLocalStorage();

						if (checkoutDepositData) {
							this.checkoutNavigationService.gotoByNavigation(
								CheckoutRouteName.CheckoutDepositPaymentFailed,
								checkoutDepositData
							);
						}
						break;
					default:
						//TODO: remove this line after BE allows new
						this.cleanMYQueryParamsLocalStorage();

						console.error("No known event");
				}
			});
	}

	//Clean Margin Trading query params from local storage
	cleanMYQueryParamsLocalStorage(): void {
		if (window.localStorage.getItem(MT_QUERY_PARAMS)) {
			window.localStorage.removeItem(MT_QUERY_PARAMS);
		}
	}

	public clearTimeout(): void {
		if (this.timer) {
			clearTimeout(this.timer);
		}
	}

	public showVisaCreditCardNotAccepted(data: CheckoutDepositData): void {
		this.checkoutNavigationService.gotoByNavigation(CheckoutRouteName.CheckoutDepositVisaCCNotSupported, {
			...data
		});
	}

	public showProcessingScreenByUrlTree(): UrlTree {
		this.startTimer();
		return this.checkoutNavigationService.gotoByUrlTree(CheckoutRouteName.CheckoutDepositProcessing);
	}

	public showProcessingScreen(): void {
		this.startTimer();
		this.checkoutNavigationService.gotoByNavigation(CheckoutRouteName.CheckoutDepositProcessing);
	}

	public getStatusFromEndpoint(fiatTransactionPid: string): Observable<string> {
		return this.apicallService.get(`payments/fiat-wallet-transaction/${fiatTransactionPid}`).pipe(
			take(1),
			map((res) => res.attributes.payment_status)
		);
	}

	private startTimer(): void {
		this.timer = setTimeout(() => {
			this.checkoutNavigationService.gotoByNavigation(CheckoutRouteName.CheckoutDepositPaymentTakingLong);
		}, 15000);
	}
}

class DepositStateSocketResponse extends Model implements HasSocketEvent {
	fiat_wallet_transactions_pid: string;
	status: string;
	eventData: SocketEvent;

	getEvent(): string {
		return this.eventData;
	}
}
